import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import HistoryBacLookupDialog from './HistoryBacLookupDialog.jsx';
import {
  downloadBillingReconciliationExportArtifactFile,
  downloadBillingReconciliationSourceFileFromHistory,
  useBillingReconciliationHistory,
  useUpdateBillingReconciliationHistoryAdjustment,
} from '../../hooks/api/useBillingReconciliationHooks.js';

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const monthLabel = (value) => {
  if (!value) return '--';
  const [year, month] = String(value).split('-');
  return new Date(Number(year), Number(month || 1) - 1, 1).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
};

export const billableHistoryChartRows = (rows = []) => rows.filter((row) => Number(row.billingAmount || 0) > 0);

const HistoryLineChart = ({ rows = [] }) => {
  const chartRows = useMemo(() => billableHistoryChartRows(rows), [rows]);
  const chart = useMemo(() => {
    const width = 760;
    const height = 220;
    const pad = { top: 24, right: 28, bottom: 38, left: 72 };
    const tickStep = 500000;
    const values = chartRows.map((row) => Number(row.billingAmount || 0));
    const min = 0;
    const max = Math.max(tickStep, Math.ceil(Math.max(...values, 1) / tickStep) * tickStep);
    const span = Math.max(1, max - min);
    const xFor = (index) => pad.left + (chartRows.length <= 1 ? 0 : index * ((width - pad.left - pad.right) / (chartRows.length - 1)));
    const yFor = (value) => pad.top + (max - value) * ((height - pad.top - pad.bottom) / span);
    const ticks = Array.from({ length: Math.floor((max - min) / tickStep) + 1 }, (_, index) => min + (index * tickStep));
    const points = chartRows.map((row, index) => ({
      x: xFor(index),
      y: yFor(Number(row.billingAmount || 0)),
      row,
    }));
    return { width, height, pad, min, max, ticks, points, path: points.map((point) => `${point.x},${point.y}`).join(' ') };
  }, [chartRows]);

  if (!chartRows.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">No non-zero final billing amounts are available for the chart yet.</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Final Billing Amount Over Time</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <svg role="img" aria-label="Final billing amount over time" viewBox={`0 0 ${chart.width} ${chart.height}`} width="100%" height="240">
            <line x1={chart.pad.left} x2={chart.width - chart.pad.right} y1={chart.height - chart.pad.bottom} y2={chart.height - chart.pad.bottom} stroke="#526071" strokeWidth="1" />
            <line x1={chart.pad.left} x2={chart.pad.left} y1={chart.pad.top} y2={chart.height - chart.pad.bottom} stroke="#526071" strokeWidth="1" />
            {chart.ticks.map((tick) => {
              const y = chart.pad.top + (chart.max - tick) * ((chart.height - chart.pad.top - chart.pad.bottom) / Math.max(1, chart.max - chart.min));
              return (
                <g key={tick}>
                  <line x1={chart.pad.left} x2={chart.width - chart.pad.right} y1={y} y2={y} stroke="#243044" strokeWidth="1" />
                  <text x={chart.pad.left - 10} y={y + 4} textAnchor="end" fill="#aeb7c4" fontSize="12">{money(tick)}</text>
                </g>
              );
            })}
            <polyline fill="none" stroke="#58a6ff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" points={chart.path} />
            {chart.points.map((point) => (
              <Tooltip
                key={point.row.serviceMonth}
                arrow
                enterDelay={0}
                title={(
                  <Stack spacing={0.25}>
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>{monthLabel(point.row.serviceMonth)}</Typography>
                    <Typography variant="caption">Final billing amount: {money(point.row.billingAmount)}</Typography>
                    <Typography variant="caption">
                      Final adjusted amount: {point.row.finalAdjustedAmount == null
                        ? `Not set (using final file ${money(point.row.finalFileAmount)})`
                        : money(point.row.finalAdjustedAmount)}
                    </Typography>
                    <Typography variant="caption">Final file amount: {money(point.row.finalFileAmount)}</Typography>
                  </Stack>
                )}
              >
                <g tabIndex={0} role="button" aria-label={`${monthLabel(point.row.serviceMonth)} final billing amount ${money(point.row.billingAmount)}`}>
                  <circle cx={point.x} cy={point.y} r="8" fill="transparent" />
                  <circle cx={point.x} cy={point.y} r="5" fill="#58a6ff" stroke="#0b1220" strokeWidth="2" />
                  <text x={point.x} y={chart.height - 12} textAnchor="middle" fill="#aeb7c4" fontSize="11">{monthLabel(point.row.serviceMonth)}</text>
                </g>
              </Tooltip>
            ))}
          </svg>
        </Box>
      </Stack>
    </Paper>
  );
};

const FileButtons = ({ files = [] }) => {
  if (!files.length) return <Typography variant="body2" color="text.secondary">No retained files</Typography>;
  const download = (file) => {
    if (file.storageKey) {
      return downloadBillingReconciliationSourceFileFromHistory({ storageKey: file.storageKey, fileName: file.fileName });
    }
    return downloadBillingReconciliationExportArtifactFile({ runId: file.runId, exportId: file.exportId, kind: file.kind || 'workbook' });
  };
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {files.map((file) => (
        <Button
          key={file.id}
          size="small"
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => download(file)}
        >
          {file.role === 'original' ? 'Original' : 'Final'}
        </Button>
      ))}
    </Stack>
  );
};

const HistoryDashboardPanel = ({ program = 'website', supported = true }) => {
  const historyQuery = useBillingReconciliationHistory({ program, limit: 240 }, { enabled: supported });
  const updateMutation = useUpdateBillingReconciliationHistoryAdjustment();
  const rows = historyQuery.data?.rows || [];
  const [drafts, setDrafts] = useState({});
  const [lookupRow, setLookupRow] = useState(null);

  const draftValueFor = (row) => (
    Object.prototype.hasOwnProperty.call(drafts, row.serviceMonth)
      ? drafts[row.serviceMonth]
      : row.finalAdjustedAmount ?? ''
  );
  const saveAdjustment = (row) => {
    const draftValue = draftValueFor(row);
    updateMutation.mutate({
      program: row.program || program,
      serviceMonth: row.serviceMonth,
      finalAdjustedAmount: draftValue === '' ? null : Number(draftValue),
    }, {
      onSuccess: () => setDrafts((current) => {
        const next = { ...current };
        delete next[row.serviceMonth];
        return next;
      }),
    });
  };
  const resetAdjustment = (row) => {
    setDrafts((current) => ({
      ...current,
      [row.serviceMonth]: '',
    }));
  };

  return (
    <Stack spacing={2}>
      {!supported && (
        <Alert severity="info">Billing history is not available for this program yet.</Alert>
      )}
      {historyQuery.isLoading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading billing history...</Typography>
        </Stack>
      )}
      {historyQuery.error && <Alert severity="error">{historyQuery.error.message}</Alert>}
      <HistoryLineChart rows={rows} />
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Billing Files</Typography>
            <Chip size="small" variant="outlined" label={`${rows.length.toLocaleString()} months`} />
          </Stack>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small" aria-label="Billing history by month">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Original file amount</TableCell>
                  <TableCell align="right">Final file amount</TableCell>
                  <TableCell align="right">Final adjusted</TableCell>
                  <TableCell align="right">Variances</TableCell>
                  <TableCell>Lookup</TableCell>
                  <TableCell>Files</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const draftValue = draftValueFor(row);
                  const dirty = String(draftValue) !== String(row.finalAdjustedAmount ?? '');
                  return (
                    <TableRow key={`${row.program}-${row.serviceMonth}`} hover>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>{monthLabel(row.serviceMonth)}</Typography>
                          <Typography variant="caption" color="text.secondary">Round {row.roundNumber}{row.importMode === 'historical_backfill' ? ' · Historical' : ''}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{money(row.originalFileAmount)}</TableCell>
                      <TableCell align="right">{money(row.finalFileAmount)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <TextField
                            size="small"
                            type="number"
                            value={draftValue}
                            placeholder={money(row.finalFileAmount)}
                            onChange={(event) => setDrafts((current) => ({ ...current, [row.serviceMonth]: event.target.value }))}
                            inputProps={{ 'aria-label': `Final adjusted amount for ${monthLabel(row.serviceMonth)}` }}
                            sx={{ width: 130 }}
                          />
                          <Tooltip title="Save final adjusted amount">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label="Save final adjusted amount"
                                disabled={!dirty || updateMutation.isPending}
                                onClick={() => saveAdjustment(row)}
                              >
                                <SaveOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Clear final adjusted amount">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Clear final adjusted amount"
                                disabled={updateMutation.isPending}
                                onClick={() => resetAdjustment(row)}
                              >
                                <UndoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{Number(row.varianceCount || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SearchOutlinedIcon />}
                          onClick={() => setLookupRow(row)}
                          aria-label={`Search BAC billing for ${monthLabel(row.serviceMonth)}`}
                        >
                          Search BAC
                        </Button>
                      </TableCell>
                      <TableCell><FileButtons files={row.files || []} /></TableCell>
                    </TableRow>
                  );
                })}
                {!rows.length && !historyQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ color: 'text.secondary' }}>No historical billing months have been imported or exported yet.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
      <HistoryBacLookupDialog
        open={Boolean(lookupRow)}
        row={lookupRow}
        onClose={() => setLookupRow(null)}
      />
    </Stack>
  );
};

export default HistoryDashboardPanel;
